import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { OutageTypePipe } from '../../pipes/outage-type.pipe';
import { PowerOutageService } from '../../power-outage.service';
import { InfoErrorStatusComponent } from './components/info-error-status/info-error-status.component';
import { MunicipalityListComponent } from './components/municipality-list/municipality-list.component';
import { OutageDescriptionComponent } from './components/outage-description/outage-description.component';
import { OutageListComponent } from './components/outage-list/outage-list.component';
import { OutsideElviaAreaComponent } from './components/outside-elvia-area/outside-elvia-area.component';
import { UnreportedOutageHelpComponent } from './components/unreported-outage-help/unreported-outage-help.component';
import { InfoContainerStore } from './info-container.store';

@Component({
  selector: 'app-info-container',
  imports: [
    DecimalPipe,
    InfoErrorStatusComponent,
    MunicipalityListComponent,
    NgTemplateOutlet,
    OutageDescriptionComponent,
    OutageListComponent,
    OutageTypePipe,
    OutsideElviaAreaComponent,
    UnreportedOutageHelpComponent,
  ],
  templateUrl: './info-container.component.html',
  styleUrl: './info-container.component.css',
  host: {
    '[class.border-top]': 'showBorderTop()',
    '[class.signal-danger]': 'borderDanger()',
    '[class.signal-warning]': 'borderWarning()',
    class: 'floating-box e-p-16',
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InfoContainerComponent {
  private readonly powerOutageService = inject(PowerOutageService);
  private readonly infoContainerStore = inject(InfoContainerStore);

  protected readonly powerOutagesResource = this.powerOutageService.powerOutagesResource;
  protected readonly view = this.infoContainerStore.view;

  readonly isOpen = this.infoContainerStore.isOpen;

  readonly municipalitiesWithOutages = this.infoContainerStore.municipalitiesWithOutages;
  readonly selectedMunicipality = this.infoContainerStore.selectedMunicipality;
  readonly selectedOutage = this.infoContainerStore.selectedOutage;

  protected readonly showBackButton = computed(() => this.isOpen() && this.view() !== 'idle');

  protected onBackClick() {
    switch (this.view()) {
      case 'outage':
        this.infoContainerStore.setSelectedOutage(undefined);
        this.infoContainerStore.setView(this.selectedMunicipality() ? 'municipality' : 'idle');
        break;
      case 'municipality':
        this.infoContainerStore.setSelectedMunicipality(undefined);
        this.infoContainerStore.setView('idle');
        break;
      case 'outsideElviaArea':
      case 'noOutage':
      case 'idle':
      default:
        this.infoContainerStore.setView('idle');
        break;
    }
  }

  protected setIsOpen(isOpen: boolean) {
    this.infoContainerStore.setIsOpen(isOpen);
  }

  readonly showBorderTop = computed(() => {
    const view = this.view();
    return view === 'outage' || view === 'outsideElviaArea' || view === 'noOutage';
  });

  readonly borderWarning = computed(() => {
    const view = this.view();
    return (view === 'outage' && this.selectedOutage()?.type === 'Planned') || view === 'noOutage';
  });

  readonly borderDanger = computed(() => {
    const view = this.view();
    return (
      (view === 'outage' && this.selectedOutage()?.type === 'Unplanned') ||
      view === 'outsideElviaArea'
    );
  });
}
